import { useGetAgency } from "@entities/agency/lib/hooks/useGetAgency";
import {
  Avatar,
  Col,
  Flex,
  Input,
  Rate,
  Row,
  Space,
  Spin,
  Tag
} from "antd";
import { useParams } from "react-router-dom";

import styles from "./styles.module.scss";

import { useDeLinkInfluencer } from "@entities/agency/lib/hooks/useDeLinkInfluencer";
import { useGetInfluencers } from "@entities/influencer";
import { useGetInfluencerFacets } from "@entities/influencer/lib/hooks/useGetFacets";
import { InfDetailsButtonV2 } from "@features/InfDetailsButtonV2";
import { ToastService } from "@shared/lib/services";
import {
  IInfluencers,
  InfluencerContact,
  InfluencerType,
  SocialMediaEntry,
} from "@shared/lib/types/influencer";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Table, TableTitleLayout } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import ColumnFilterWrapper from "@shared/ui/filters/ColumnFilterWrapper/ColumnFilterWrapper";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { ColumnsType } from "antd/es/table";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";

export function AgencyInfluencers() {
  const { id } = useParams();

  const { agency, isFetching } = useGetAgency(id || "");

  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedSocialMediaName, setSelectedSocialMediaName] = useState<
    string[]
  >([]);
  const [influencerType, setInfluencerType] = useState<InfluencerType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [rating, setRating] = useState<number | undefined>(undefined);

  const { influencers, isFetching: isFetchingInfluencers } = useGetInfluencers(
    searchQuery,
    selectedSocialMediaName,
    selectedLocations,
    rating,
    selectedCategory,
    influencerType,
    pageSize,
    paegNo,
    [id || ""]
  );

  const { deLinkInfluencer, isDeLinking, isDeLinked, variables } =
    useDeLinkInfluencer();

  const { facets } = useGetInfluencerFacets();

  const socialMediasToDisplay =
    (facets?.socialMediaPlatforms || [])?.map((item) => item._id) || [];
  const categoriesToDisplay =
    (facets?.category || [])?.map((item) => item._id) || [];
  const typeToDisplay =
    (facets?.type || [])?.map((item) => item._id as InfluencerType) || [];

  const locationToDisplay =
    (facets?.location || [])?.map((item) => item._id) || [];

  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleSocialMediaChange = (value: string[] | undefined) => {
    if (!value) return setSelectedSocialMediaName([]);
    setSelectedSocialMediaName(value);
  };

  const handleTypeChange = (value: InfluencerType[] | undefined) => {
    if (!value) return setInfluencerType([]);
    setInfluencerType(value);
  };
  const handleRatingChange = (value: number | undefined) => {
    if (!value) return setRating(undefined);
    setRating(value);
  };
  const handleLocationChange = (value: string[] | undefined) => {
    if (!value) return setSelectedLocations([]);
    setSelectedLocations(value);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  useEffect(() => {
    setPageNo(1);
  }, [
    searchQuery,
    selectedSocialMediaName,
    selectedLocations,
    rating,
    selectedCategory,
    influencerType,
    pageSize,
  ]);

  const columns: ColumnsType<IInfluencers> = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "name",
      render: (_: string, record: IInfluencers) => (
        <Row align="middle" gutter={[8, 8]} style={{ cursor: "pointer" }}>
          <Col>
            <Avatar
              src={record.profilePicUrl}
              alt="Profile Picture"
              size={40}
            />
          </Col>
          <Col>
            {record.firstName} {record.lastName}
          </Col>
          <Col>
            <InfDetailsButtonV2
              creatorInfo={{
                name: `${record.firstName} ${record.lastName}`,
                image: record.profilePicUrl,
              }}
              socialMediaInfluencerReq={{
                influencerId: record.influencerId,
              }}
            />
          </Col>
        </Row>
      ),
    },

    {
      title: "Social Media",
      dataIndex: "socialMedias",
      key: "socialMedias",
      filterCount: selectedSocialMediaName.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Social Media"
          placeholder="Select social media"
          initialTagCount={3}
          itemsToDisplay={socialMediasToDisplay}
          selectedItems={selectedSocialMediaName}
          handleSelectionChange={handleSocialMediaChange}
          props={props}
        />
      ),
      render: (socialMedias: SocialMediaEntry[]) => {
        return (
          <div className={styles.grid}>
            {(socialMedias || [])?.map((item) => (
              <Space>
                {getPlatformIcon(item.platform)} <div>{item.userName}</div>
              </Space>
            ))}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filterCount: influencerType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Type"
          placeholder="Select type"
          initialTagCount={3}
          itemsToDisplay={typeToDisplay}
          selectedItems={influencerType}
          handleSelectionChange={(value: string[]) => {
            handleTypeChange(value as InfluencerType[]);
          }}
          props={props}
        />
      ),
      render: (status: InfluencerType) => <Tag color={"gray"}>{status}</Tag>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filterCount: selectedCategory.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Category"
          placeholder="Select category"
          initialTagCount={3}
          itemsToDisplay={categoriesToDisplay}
          selectedItems={selectedCategory}
          handleSelectionChange={setSelectedCategory}
          props={props}
        />
      ),
      render: (status: string[]) => (
        <div className={styles.grid}>
          {(status || []).map((item) => (
            <Tag color={"gray"} style={{ width: "max-content" }}>
              {item}
            </Tag>
          ))}
        </div>
      ),
    },

    {
      title: "Contact",
      dataIndex: "contacts",
      key: "contacts",

      render: (contacts: InfluencerContact[]) => {
        return (
          <div className={styles.grid}>
            {(contacts || []).slice(0, 2).map((item) => (
              <>
                {item?.phoneNumber && (
                  <div>
                    {item?.countryCode} {item.phoneNumber}
                  </div>
                )}
                {item?.emailId && <div>{item.emailId}</div>}
              </>
            ))}
            {(contacts || []).length > 2 && <div>...</div>}
          </div>
        );
      },
    },

    {
      title: "Rating",
      dataIndex: "avgReview",
      key: "avgReview",
      width: 200,
      filterCount: rating ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        const FilterDropdownContent = () => {
          const [tempRating, setTempRating] = useState<number>(rating || 0);

          return (
            <ColumnFilterWrapper
              title="Rating"
              filterCount={tempRating ? 1 : 0}
              isFiltered={tempRating > 0}
              onReset={() => {
                setTempRating(0);
                handleRatingChange(undefined);
                props.confirm();
              }}
              onApply={() => {
                handleRatingChange(tempRating);
                props.confirm();
              }}
              onClose={props.close}
            >
              <div style={{ padding: "8px 4px", display: "flex", justifyContent: "center" }}>
                <Rate
                  allowHalf
                  value={tempRating}
                  onChange={(value) => setTempRating(value)}
                />
              </div>
            </ColumnFilterWrapper>
          );
        };

        return <FilterDropdownContent />;
      },
      render: (avgReview: number) => (
        <Rate allowHalf disabled value={avgReview} />
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      filterCount: selectedLocations.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Location"
          placeholder="Select location"
          initialTagCount={3}
          itemsToDisplay={locationToDisplay}
          selectedItems={selectedLocations}
          handleSelectionChange={handleLocationChange}
          props={props}
        />
      ),
      render: (location: string[]) => (
        <div className={styles.grid}>
          {(location || []).map((item) => (
            <div>{item}</div>
          ))}
        </div>
      ),
    },
    {
      title: "Action",

      render: (_: string, record: IInfluencers) => (
        <Button
          type="link"
          danger
          loading={
            isDeLinking && variables?.influencerId === record.influencerId
          }
          onClick={() =>
            deLinkInfluencer({
              agencyId: id || "",
              influencerId: record.influencerId,
            })
          }
        >
          De-link
        </Button>
      ),
    },
  ];
  useEffect(() => {
    if (isDeLinked) {
      ToastService.success("Influencer de-linked successfully");
    }
  }, [isDeLinked]);

  if (isFetching) {
    return (
      <Row justify="center" align="middle" style={{ height: "100vh" }}>
        <Spin />
      </Row>
    );
  }

  return (
    <div>
      <div className={styles.title}>Agency - {agency?.name}</div>

        <Table
          dataSource={influencers?.data}
          columns={columns}
          title={() => <TableTitleLayout 
            filterComponent={<Flex justify="space-between" style={{width: "100%"}}>
              <Input
            placeholder="Search by name of influencer or social media"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: 350 }}
            prefix={<Search size={14} />}
          />
            </Flex>}
            totalText={`${influencers?.pagination?.totalCount || 0} Records found`}
          />}
          loading={isFetchingInfluencers}
          pagination={{
            current: paegNo,
            pageSize: pageSize,
            total: influencers?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
          scroll={{
            x: "max-content"
          }}
        />
    </div>
  );
}
